import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FlagGB = ({ className = 'w-5 h-3.5 rounded-sm shrink-0' }) => (
  <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <clipPath id="gb-s">
      <path d="M0 0v30h60V0z" />
    </clipPath>
    <clipPath id="gb-t">
      <path d="M30 15h30v15zv15H0zH30V0H0z" />
    </clipPath>
    <g clipPath="url(#gb-s)">
      <path d="M0 0v30h60V0z" fill="#012169" />
      <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6" />
      <path d="M0 0l60 30m0-30L0 30" clipPath="url(#gb-t)" stroke="#C8102E" strokeWidth="4" />
      <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10" />
      <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6" />
    </g>
  </svg>
);

const FlagFR = ({ className = 'w-5 h-3.5 rounded-sm shrink-0' }) => (
  <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="1" height="2" fill="#002395" />
    <rect x="1" width="1" height="2" fill="#fff" />
    <rect x="2" width="1" height="2" fill="#ED2939" />
  </svg>
);

const LANGUAGES = [
  { code: 'en', short: 'EN', label: 'English', Flag: FlagGB },
  { code: 'fr', short: 'FR', label: 'Français', Flag: FlagFR },
];

const LanguageSwitcher = ({ className = '', fullWidth = false }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const currentCode = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const current = LANGUAGES.find((lang) => lang.code === currentCode) || LANGUAGES[0];
  const CurrentFlag = current.Flag;

  const changeLanguage = (code) => {
    if (code !== currentCode) {
      i18n.changeLanguage(code);
    }
    setOpen(false);
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={`relative ${fullWidth ? 'w-full' : 'shrink-0'} ${className}`}
    >
      <button
        type="button"
        id="language-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls="language-switcher-menu"
        aria-label={t('languageSwitcher.label')}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-2 rounded-lg border border-secondary/30 bg-primary/50 px-2.5 py-2 min-h-[40px] text-sm font-semibold text-textPrimary transition-colors hover:border-secondary/50 hover:bg-primary/70 focus:outline-none focus:border-secondary focus:ring-1 focus:ring-secondary/40 ${
          fullWidth ? 'w-full justify-between' : ''
        }`}
      >
        <span className="flex items-center gap-2 min-w-0">
          <CurrentFlag />
          <span className="font-mono tracking-wide text-secondary">{current.short}</span>
        </span>
        <ChevronDown
          className={`w-4 h-4 text-textSecondary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <ul
          id="language-switcher-menu"
          role="listbox"
          aria-labelledby="language-switcher-button"
          className={`absolute z-[60] mt-1.5 overflow-hidden rounded-lg border border-secondary/30 bg-primary/95 backdrop-blur-md shadow-lg shadow-black/20 ${
            fullWidth ? 'left-0 right-0' : 'right-0 min-w-[160px]'
          }`}
        >
          {LANGUAGES.map((lang) => {
            const isActive = currentCode === lang.code;
            const OptionFlag = lang.Flag;

            return (
              <li key={lang.code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-secondary/15 text-secondary'
                      : 'text-textPrimary hover:bg-secondary/10 hover:text-secondary'
                  }`}
                >
                  <OptionFlag />
                  <span className="font-mono font-semibold tracking-wide">{lang.short}</span>
                  <span className="text-textSecondary">{lang.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
